import React, { useCallback, useMemo, useRef, useState } from 'react';
import { Calendar, dateFnsLocalizer } from 'react-big-calendar';
import {
  addDays,
  compareAsc,
  differenceInDays,
  endOfDay,
  endOfMonth,
  endOfWeek,
  format,
  formatISO,
  getDay,
  isWithinInterval,
  parse,
  parseISO,
  startOfDay,
  startOfMonth,
  startOfWeek,
} from 'date-fns';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import { useEffect } from 'react';
import { post } from '../../Auths/AuthToken';
import { Link } from 'react-router-dom';
import { DEFAULT_DATE_FORMATE, DEFAULT_LOCAL_PATH } from '../../../constents';
import CalanderHeader from './CalanderHeader';
import ReactDatePicker from 'react-datepicker';
import AddTaskPopup from './AddTaskPopup';
import WeeklySchedule from './WeeklySchedule';
import { useDispatch, useSelector } from 'react-redux';
import { setCalPeriod } from '../../../redux/slices/scheduler/calendarSlice';
import {
  setGlobalAppId,
  setAppPatSerchPopup,
  setAppPopup,
  setGrpApp,
  updateSearchParams,
} from '../../../redux/slices/scheduler/appointmentSlice';
import { setSeltSerchPat } from '../../../redux/slices/patient/patientSlice';
import setUiMessage from '../../../Utilities/Tostify';
import AlertDialog from '../../../Utilities/AlertDialog';
import { useAppointmentStatus } from '../../../hooks/useAppointmentStatus';
import { formatTime } from '../../../Utilities/Utilities';

const GRAY_DAY = {
  className: 'blocked-day',
  style: { pointerEvents: 'none', backgroundColor: '#7a7e7c' },
};

const GRAY_SLOT = { style: { backgroundColor: 'gray', color: 'white' } };

function BigCalander() {
  const clickRef = useRef(null);
  const [allData, setAllData] = useState('');
  const [allBlockData, setAllBlockData] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(undefined);
  const [eventData, setEventData] = useState(null);
  const [locList, setLocList] = useState();
  const [facilitySetting, setFacilitySetting] = useState(null);
  const [calTime, setCalTime] = useState({
    startTime: null,
    endTime: null,
    scrliiTime: null,
  });

  const dispatch = useDispatch();
  const { user } = useSelector((store) => store.auth);
  const { calPeriodFilter } = useSelector((store) => store.calander);
  const { appSearchPrams } = useSelector((store) => store.appointment);
  const { globalReload, defaultTimeFormat } = useSelector((store) => store.app);

  const [allResource, setAllResource] = useState(null);

  const [addTask, setAddTask] = useState(false);
  const [reload, setReload] = useState(false);
  const [weeklySchedule, setWeeklySchedule] = useState(false);

  const { isDialogOpen, checkAppState, closeDialog } = useAppointmentStatus(
    post,
    setUiMessage,
  );

  const localizer = dateFnsLocalizer({
    format,
    parse,
    startOfWeek: (date) =>
      startOfWeek(date, {
        weekStartsOn: facilitySetting?.facility_start_day === '1' ? 1 : 0,
      }),
    getDay,
    locales: {
      'en-US': require('date-fns/locale/en-US'),
    },
  });

  const [cancelCheck, setCancelCheck] = useState(false);
  const [rescheduleCheck, setRescheduleCheck] = useState(false);
  const [calPeriod, setCalPeriodLocal] = useState({
    name: calPeriodFilter.name || 'week',
    startDate: new Date(calPeriodFilter.startDate),
    endDate: new Date(calPeriodFilter.endDate),
  });

  useEffect(() => {
    setCalPeriodLocal({
      name: calPeriodFilter.name || 'week',
      startDate: new Date(calPeriodFilter.startDate),
      endDate: new Date(calPeriodFilter.endDate),
    });
  }, [calPeriodFilter]);

  // Sync initial week with facility settings
  useEffect(() => {
    if (facilitySetting && !window.hasCorrectedWeekStart) {
      window.hasCorrectedWeekStart = true;
      const weekStartsOn = facilitySetting.facility_start_day === '1' ? 1 : 0;
      const expectedStart = formatISO(
        startOfWeek(new Date(), { weekStartsOn }),
      );
      const expectedEnd = formatISO(endOfWeek(new Date(), { weekStartsOn }));

      if (
        calPeriodFilter.startDate !== expectedStart &&
        calPeriodFilter.name === 'week'
      ) {
        dispatch(
          setCalPeriod({
            name: 'week',
            startDate: expectedStart,
            endDate: expectedEnd,
          }),
        );
      }
    }
  }, [
    facilitySetting,
    dispatch,
    calPeriodFilter.startDate,
    calPeriodFilter.name,
  ]);

  const parseTime = useCallback(
    (timeStr, refDate = calPeriod.startDate) => {
      if (!timeStr || timeStr === 'NULL') return null;
      let parsed = null;

      if (
        timeStr.includes(' ') &&
        timeStr.includes('-') &&
        timeStr.includes(':')
      ) {
        const timePart = timeStr.split(' ')[1];
        parsed = parse(timePart, 'HH:mm:ss', refDate);
      } else if (timeStr.includes(':')) {
        if (timeStr.includes('AM') || timeStr.includes('PM')) {
          parsed = parse(timeStr, 'h:mm aa', refDate);
        } else {
          const parts = timeStr.split(':');
          if (parts.length === 3) {
            parsed = parse(timeStr, 'HH:mm:ss', refDate);
          } else if (parts.length === 2) {
            parsed = parse(timeStr, 'HH:mm', refDate);
          }
        }
      } else {
        parsed = new Date(timeStr);
      }

      const isValidDate = parsed && !isNaN(parsed.getTime());
      return isValidDate ? parsed : null;
    },
    [calPeriod.startDate],
  );

  // ─── getActiveSchedule ───────────────────────────────────────────────────────
  // For a given date, returns which schedule wins (priority order below) and
  // whether the date is past the regular schedule's end_date.
  //
  // Priority 1 (highest) → Temporary Override  (schedule_type = '2')
  // Priority 2           → Weekly Override     (schedule_type = '1')
  // Priority 3 (lowest)  → Regular Schedule   (schedule_type = '0')
  //
  // Returns: { schedule, isAfterEnd }
  //   schedule   = the winning row, or null if no schedule exists
  //   isAfterEnd = true when schedule is Regular AND date > its end_date
  // ─────────────────────────────────────────────────────────────────────────────
  const getActiveSchedule = useCallback(
    (date) => {
      const dayOfWeek = date.getDay() === 0 ? 7 : date.getDay();
      const dateMs = startOfDay(date).getTime();

      const overrides = Array.isArray(allData?.overrideSetting)
        ? allData.overrideSetting
        : [allData?.overrideSetting].filter(Boolean);

      // Check if an override row is active for this date + day
      const isActive = (o, type) => {
        if (!o?.start_date || !o?.end_date || String(o?.schedule_type) !== String(type))
          return false;
        const s = startOfDay(new Date(o.start_date)).getTime();
        const e = startOfDay(new Date(o.end_date)).getTime();
        if (isNaN(s) || isNaN(e)) return false;
        return parseInt(o.day_id) === dayOfWeek && dateMs >= s && dateMs <= e;
      };

      // Priority 1: Temporary Override
      const tempOverride = overrides.find((o) => isActive(o, '2'));
      if (tempOverride) return { schedule: tempOverride, isAfterEnd: false };

      // Priority 2: Weekly Override
      const weeklyOverride = overrides.find((o) => isActive(o, '1'));
      if (weeklyOverride)
        return { schedule: weeklyOverride, isAfterEnd: false };
      // FWO date-range active but no row for this weekday → treat as closed
      if (
        overrides.some((o) => {
          if (!o?.start_date || !o?.end_date || String(o?.schedule_type) !== '1')
            return false;
          const s = startOfDay(new Date(o.start_date)).getTime();
          const e = startOfDay(new Date(o.end_date)).getTime();
          return !isNaN(s) && !isNaN(e) && dateMs >= s && dateMs <= e;
        })
      ) {
        return { schedule: { is_closed: '0' }, isAfterEnd: false };
      }

      // Priority 3: Regular Schedule
      const regularList = Array.isArray(allData?.providerScheduleEnd)
        ? allData.providerScheduleEnd
        : allData?.providerScheduleEnd
          ? [allData.providerScheduleEnd]
          : [];

      const regular = regularList.find((s) => parseInt(s.day_id) === dayOfWeek);
      if (regular) {
        const endStr = regular.end_date;
        const hasEnd =
          endStr &&
          endStr !== 'NULL' &&
          endStr !== '0000-00-00' &&
          endStr !== '';
        const isAfterEnd =
          hasEnd && startOfDay(date) > startOfDay(new Date(endStr));
        return { schedule: regular, isAfterEnd };
      }

      return { schedule: null, isAfterEnd: false };
    },
    [allData?.overrideSetting, allData?.providerScheduleEnd],
  );

  useEffect(() => {
    /**
     * What Is This?
     * This is to prevent a memory leak, in the off chance that you
     * teardown your interface prior to the timed method being called.
     */
    return () => {
      window.clearTimeout(clickRef?.current);
    };
  }, []);

  const onSelectEvent = useCallback((event) => {
    if (event.isVirtual) return;
    window.clearTimeout(clickRef?.current);
    clickRef.current = window.setTimeout(() => {
      if (event?.is_task === '1') {
        const isPast =
          startOfDay(new Date(event.start)).getTime() <
          startOfDay(new Date()).getTime();
        if (isPast) {
          setUiMessage('err', 'Past date tasks cannot be edited.');
          return;
        }
        setAddTask(true);
      } else {
        setSelectedEvent(event);
      }
      setEventData(event);
    }, 250);
  }, []);

  const onDoubleClickEvent = useCallback(
    (calEvent) => {
      if (calEvent?.is_task === '1') return;
      window.clearTimeout(clickRef?.current);
      clickRef.current = window.setTimeout(() => {
        checkAppState(calEvent?.appData?.appId, () => {
          dispatch(setGrpApp(false));
          dispatch(setAppPopup(true));
          dispatch(setGlobalAppId(calEvent?.appData?.appId));
        });
      }, 250);
    },
    [dispatch, checkAppState],
  );

  useEffect(() => {
    (async () => {
      const response = await post('api/auth/appCalanderData', {
        resourceId: appSearchPrams.allProviders,
        locationId: appSearchPrams.clinicLocation,
        start: format(new Date(calPeriodFilter.startDate), 'yyyy-MM-dd'),
        end: format(new Date(calPeriodFilter.endDate), 'yyyy-MM-dd'),
        staffUserId: user?.id,
      });
      if (response.status === 200) {
        setAllData(response?.data);
        setAllBlockData(response?.data?.eventsClose || []);
        setLocList(response?.data?.locList);
        setAllResource(response?.data?.allResource);
        setFacilitySetting(response?.data?.facilitySetting || []);
        setCalTime({
          startTime: response?.data?.facilitySetting?.facility_first_hour,
          endTime: response?.data?.facilitySetting?.facility_last_hour,
          scrliiTime: response?.data?.facilitySetting?.facility_scroll_hour,
        });
      }
    })();
  }, [calPeriodFilter, appSearchPrams, globalReload, user, reload]);

  const onRangeChange = useCallback(
    (range, view) => {
      if (view) {
        if (range.length === 1 && calPeriodFilter.name !== 'day') {
          dispatch(
            setCalPeriod({
              name: 'day',
              startDate: formatISO(new Date(range[0])),
              endDate: formatISO(new Date(range[0])),
            }),
          );
        } else if (range.length === 7 && calPeriodFilter.name !== 'week') {
          dispatch(
            setCalPeriod({
              name: 'week',
              startDate: formatISO(
                startOfWeek(new Date(range[0]), {
                  weekStartsOn:
                    facilitySetting?.facility_start_day === '1' ? 1 : 0,
                }),
              ),
              endDate: formatISO(
                endOfWeek(new Date(range[6]), {
                  weekStartsOn:
                    facilitySetting?.facility_start_day === '1' ? 1 : 0,
                }),
              ),
            }),
          );
        } else if (
          range.length !== 1 &&
          range.length !== 7 &&
          calPeriodFilter.name !== 'month'
        ) {
          const totalDays = differenceInDays(
            new Date(range.end),
            new Date(range.start),
          );
          const safeStart = new Date(
            new Date(range.start).getFullYear(),
            new Date(range.start).getMonth(),
            new Date(range.start).getDate(),
            12,
          );
          const middleDate = addDays(safeStart, Math.floor(totalDays / 2));
          const currentMonthStart = startOfMonth(middleDate);
          const currentMonthEnd = endOfMonth(middleDate);
          dispatch(
            setCalPeriod({
              name: 'month',
              startDate: formatISO(startOfMonth(new Date(currentMonthStart))),
              endDate: formatISO(endOfMonth(new Date(currentMonthEnd))),
            }),
          );
        }
      }
    },
    [calPeriodFilter.name, dispatch, facilitySetting?.facility_start_day],
  );

  const onNavigate = (newDate, view, action) => {
    if (view === 'day') {
      dispatch(
        setCalPeriod({
          name: 'day',
          startDate: formatISO(newDate),
          endDate: formatISO(newDate),
        }),
      );
    } else if (view === 'week') {
      dispatch(
        setCalPeriod({
          name: 'week',
          startDate: formatISO(
            startOfWeek(newDate, {
              weekStartsOn: facilitySetting?.facility_start_day === '1' ? 1 : 0,
            }),
          ),
          endDate: formatISO(
            endOfWeek(newDate, {
              weekStartsOn: facilitySetting?.facility_start_day === '1' ? 1 : 0,
            }),
          ),
        }),
      );
    } else if (view === 'month') {
      dispatch(
        setCalPeriod({
          name: 'month',
          startDate: formatISO(startOfMonth(newDate)),
          endDate: formatISO(endOfMonth(newDate)),
        }),
      );
    }
  };

  const handleLocation = useCallback(
    (e) => {
      dispatch(
        updateSearchParams({
          key: 'clinicLocation',
          value: e.target.value,
        }),
      );
    },
    [dispatch],
  );
  const handleResource = useCallback(
    (e) => {
      dispatch(
        updateSearchParams({
          key: 'allProviders',
          value: e.target.value,
        }),
      );
    },
    [dispatch],
  );

  const handleTitlePpUpClose = (event) => {
    setSelectedEvent(undefined);
    setEventData(null);
  };

  const handleSelectSlot = useCallback(
    (event) => {
      if (calPeriodFilter?.name === 'month' || event.start.getHours() >= 7) {
        dispatch(setGrpApp(false));
        dispatch(setAppPopup(true));
        dispatch(
          setSeltSerchPat({
            patId: null,
            getPatientType: null,
            getNewPatientType: null,
            patName: null,
            appDate: formatISO(new Date(event?.start)),
            appTime: formatISO(new Date(event?.end)),
          }),
        );
        dispatch(setAppPatSerchPopup(true));
      }
    },
    [dispatch, calPeriodFilter],
  );

  const TitlePpup = () => {
    return (
      <div id="overlay" onClick={(e) => handleTitlePpUpClose(e)}>
        <div className="title-popup-container" id="text">
          <div className="title-popup-head">
            <span className="title-popup-head-title">
              {eventData?.appData?.appTime}
            </span>
            <span
              className="title-popup-head-close"
              onClick={(e) => handleTitlePpUpClose(e)}
            >
              x
            </span>
          </div>
          <div className="tp-name-block">
            <div className="schToolTipTopLeftRow">
              <Link
                to={`${DEFAULT_LOCAL_PATH}/patient/counNote/${eventData?.appData?.appPatientId}`}
              >
                <div className="schToolTipInnerRow" title="Go To Patient Chart">
                  {eventData?.appData?.patientFullName}
                  {eventData?.appData?.chartNumber
                    ? '[' + eventData?.appData?.chartNumber + ']'
                    : ''}
                </div>
              </Link>
              <div className="schToolTipInnerRow">
                {eventData?.appData?.age +
                  ', ' +
                  eventData?.appData?.gender +
                  ', ' +
                  eventData?.appData?.dob}
              </div>
            </div>
            <img
              src={eventData?.appData?.photograph}
              height="40"
              width="46"
              alt=""
            />
          </div>
          <div className="title-popup-row">
            <span>H: {eventData?.appData?.homePhoneOfPatient}</span>
            <span>M: {eventData?.appData?.mobilePhoneOfPatient}</span>
          </div>
          <div className="title-popup-row">e: {eventData?.appData?.email}</div>
          <div className="title-popup-row">{eventData?.appData?.visitType}</div>
          <div className="title-popup-row">
            {eventData?.appData?.appDate + ', ' + eventData?.appData?.appTime}
          </div>
          <div className="title-popup-row">
            P:{eventData?.appData?.provider_name}
            {', '}R:
            {eventData?.appData?.doctor_name}
          </div>
          <div className="title-popup-row">
            Status: {eventData?.appData?.status}
          </div>
          <div className="title-popup-row">
            Payment type: {eventData?.appData?.paymentType}
          </div>
          <div className="title-popup-row">
            Location: {eventData?.appData?.locationCode}
          </div>
          <div className="title-popup-row">
            Reason {eventData?.appData?.visitNote}
          </div>
        </div>
      </div>
    );
  };

  const handleChange = useCallback(
    (date) => {
      window.clearTimeout(clickRef?.current);
      clickRef.current = window.setTimeout(() => {
        dispatch(
          setCalPeriod({
            name: 'day',
            startDate: formatISO(date),
            endDate: formatISO(date),
          }),
        );
      }, 250);
    },
    [dispatch],
  );

  const combinedEvents = useMemo(() => {
    let filtered = [];
    if (allData?.events && allData.events.length > 0) {
      filtered = allData.events.filter((event) => {
        if (cancelCheck && !rescheduleCheck) {
          return event.appData?.status !== 'Rescheduled';
        } else if (!cancelCheck && rescheduleCheck) {
          return event.appData?.status !== 'Canceled';
        } else if (cancelCheck && rescheduleCheck) {
          return true;
        } else {
          return (
            event.appData?.status !== 'Canceled' &&
            event.appData?.status !== 'Rescheduled'
          );
        }
      });
    }

    if (calPeriod.name === 'month') {
      return filtered;
    }

    const virtualEvents = [];
    const viewStart = startOfDay(new Date(calPeriod.startDate));
    const viewEnd = endOfDay(new Date(calPeriod.endDate));

    let cur = new Date(viewStart);
    while (cur <= viewEnd) {
      const currentCur = cur;
      const dayOfWeek = currentCur.getDay() === 0 ? 7 : currentCur.getDay();
      const overrides = Array.isArray(allData?.overrideSetting)
        ? allData.overrideSetting
        : [allData?.overrideSetting].filter(Boolean);

      // Find Priority Base for Breaks
      const type1Override = overrides.find((o) => {
        if (!o?.start_date || !o?.end_date || String(o?.schedule_type) !== '1')
          return false;
        const d = startOfDay(currentCur).getTime();
        const s = startOfDay(new Date(o.start_date)).getTime();
        const e = startOfDay(new Date(o.end_date)).getTime();
        if (isNaN(s) || isNaN(e)) return false;
        return parseInt(o?.day_id) === dayOfWeek && d >= s && d <= e;
      });

      const providerRegularSetting = Array.isArray(allData?.providerScheduleEnd)
        ? allData.providerScheduleEnd.find(
            (s) => parseInt(s.day_id) === dayOfWeek,
          )
        : parseInt(allData?.providerScheduleEnd?.day_id) === dayOfWeek
          ? allData?.providerScheduleEnd
          : null;

      // Check if currentCur is past the provider's schedule end_date
      const providerEndDateStr = providerRegularSetting?.end_date;
      const isAfterScheduleEnd =
        providerEndDateStr &&
        providerEndDateStr !== 'NULL' &&
        providerEndDateStr !== '0000-00-00' &&
        providerEndDateStr !== '' &&
        startOfDay(currentCur) > startOfDay(new Date(providerEndDateStr));

      // Breaks: type1Override (weekly) always wins.
      // Regular schedule (type0) only used if NOT past its end_date.
      const activeBase =
        type1Override || (!isAfterScheduleEnd ? providerRegularSetting : null);

      let break1 = { start: null, end: null, title: 'Break' };
      let break2 = { start: null, end: null, title: 'Break' };

      const hasOpenTaskForDate = allData?.openMyCalander?.some((interval) => {
        const start = parseISO(interval.start_time);
        return format(start, 'yyyy-MM-dd') === format(currentCur, 'yyyy-MM-dd');
      });

      if (activeBase && String(activeBase.is_closed) === '1' && !hasOpenTaskForDate) {
        break1 = {
          start: activeBase.break_first_start_time,
          end: activeBase.break_first_end_time,
          title: activeBase.break_first_name || 'Break',
        };
        break2 = {
          start: activeBase.break_second_start_time,
          end: activeBase.break_second_end_time,
          title: activeBase.break_second_name || 'Break',
        };
      }

      const dailyBreaks = [break1, break2];

      dailyBreaks.forEach((b) => {
        if (b.start && b.end) {
          const s = parseTime(b.start, currentCur);
          const e = parseTime(b.end, currentCur);
          if (s && e && compareAsc(s, e) < 0) {
            virtualEvents.push({
              start: s,
              end: e,
              title: b.title,
              isVirtual: true,
              type: 'break',
            });
          }
        }
      });
      cur = addDays(cur, 1);
    }

    allBlockData?.forEach((item) => {
      if (item.is_task === '2' && item.start && item.end) {
        virtualEvents.push({
          start: parseISO(item.start),
          end: parseISO(item.end),
          title: item.title || 'Blocked',
          isVirtual: true,
          type: 'block',
        });
      }
    });

    return [...filtered, ...virtualEvents];
  }, [
    allData,
    allBlockData,
    calPeriod,
    parseTime,
    cancelCheck,
    rescheduleCheck,
  ]);

  // Returns true if slotDate falls before workOpen or after/at workClose
  const isSlotOutsideHours = (slotDate, workOpen, workClose) => {
    if (!workOpen && !workClose) return false;
    const h = slotDate.getHours();
    const m = slotDate.getMinutes();
    if (
      workOpen &&
      (h < workOpen.getHours() ||
        (h === workOpen.getHours() && m < workOpen.getMinutes()))
    )
      return true;
    if (
      workClose &&
      (h > workClose.getHours() ||
        (h === workClose.getHours() && m >= workClose.getMinutes()))
    )
      return true;
    return false;
  };

  // 0 or '' = All providers (do not use truthy check — 0 is falsy)
  const hasProvider =
    appSearchPrams?.allProviders != null &&
    String(appSearchPrams.allProviders) !== '' &&
    String(appSearchPrams.allProviders) !== '0';

  const dayPropGetter = useCallback(
    (date) => {
      if (!date || isNaN(new Date(date).getTime())) return {};
      const dayOfWeek = date.getDay() === 0 ? 7 : date.getDay();
      const dateStr = format(date, 'yyyy-MM-dd');

      // Step 1: "Open My Calendar" task on this day → always show open
      const hasOpenTask = allData?.openMyCalander?.some(
        (interval) =>
          format(parseISO(interval.start_time), 'yyyy-MM-dd') === dateStr,
      );
      if (hasOpenTask) return {};

      // Step 2: Get the winning schedule (Temp Override > Weekly Override > Regular)
      const { schedule, isAfterEnd } = getActiveSchedule(date);

      // Step 3: No schedule → gray when a provider is selected (facility ≠ available)
      if (!schedule) {
        if (hasProvider) return GRAY_DAY;
        const facilityDay = allData?.facilityHourSetting?.find(
          (f) => parseInt(f.day_id) === dayOfWeek,
        );
        if (!facilityDay || String(facilityDay.is_closed) === '0') return GRAY_DAY;
        return {};
      }

      // Step 4: Regular schedule has expired (past end_date) → gray
      if (isAfterEnd) return GRAY_DAY;

      // Step 5: Schedule says this day is closed → gray
      if (String(schedule.is_closed) === '0') return GRAY_DAY;

      // Step 6: Day is open
      return {};
    },
    [allData, getActiveSchedule, hasProvider],
  );

  // Library bug: dayPropGetter gets max-time, not column date. Fix using real date.
  const dayColumnWrapper = useMemo(
    () =>
      React.forwardRef(function ProviderDayColumn(
        { children, className, style, date },
        ref,
      ) {
        const avail = date ? dayPropGetter(date) : {};
        return (
          <div
            ref={ref}
            className={[className, avail.className].filter(Boolean).join(' ')}
            style={{ ...style, ...(avail.style || {}) }}
          >
            {children}
          </div>
        );
      }),
    [dayPropGetter],
  );

  const eventPropGetter = useCallback((event) => {
    let style = {
      color: 'black',
      backgroundColor: 'blue',
      pointerEvents: 'auto',
    };

    if (event.isVirtual) {
      if (event.type === 'break') {
        style = {
          backgroundColor: 'powderblue',
          color: 'black',
          pointerEvents: 'none',
          border: '1px solid #bdd1d1',
          fontSize: '11px',
          padding: '2px',
        };
      } else if (event.type === 'block') {
        style = {
          backgroundColor: '#7a7e7c',
          color: 'white',
          pointerEvents: 'none',
          fontSize: '11px',
          padding: '2px',
        };
      }
    } else {
      if (event.app_status === '1') {
        style.backgroundColor = 'gray';
      } else if (event.app_status === '2') {
        style.backgroundColor = 'gray';
      } else if (event.app_status === '3') {
        style.backgroundColor = 'gray';
      } else {
        if (event.app_color) {
          style.backgroundColor = event.app_color;
        }
      }
    }

    return { style };
  }, []);

 

  const slotPropGetter = useCallback(
    (date) => {
      // Month view doesn't use slot coloring
      if (calPeriod.name === 'month') return {};
      if (!date || isNaN(new Date(date).getTime())) return {};

      const dayOfWeek = date.getDay() === 0 ? 7 : date.getDay();

      // Step 1: "Open My Calendar" task covers this exact slot → white/open
      const isInOpenTask = allData?.openMyCalander?.some((interval) => {
        const start = parseISO(interval.start_time);
        const end = parseISO(interval.end_time);
        return (
          compareAsc(start, end) < 0 &&
          isWithinInterval(date, { start, end })
        );
      });
      if (isInOpenTask)
        return { style: { backgroundColor: '#fff', color: 'black' } };

      const hasOpenTaskForDate = allData?.openMyCalander?.some((interval) => {
        const start = parseISO(interval.start_time);
        return format(start, 'yyyy-MM-dd') === format(date, 'yyyy-MM-dd');
      });
      if (hasOpenTaskForDate && !isInOpenTask) return GRAY_SLOT;

      // Step 2: Blocked task (block event) covers this slot → gray
      const isInBlockedTask = allBlockData?.some(
        (item) =>
          item.is_task === '2' &&
          item.start &&
          item.end &&
          isWithinInterval(date, {
            start: parseISO(item.start),
            end: parseISO(item.end),
          }),
      );
      if (isInBlockedTask)
        return { style: { backgroundColor: '#7a7e7c', color: 'white' } };

      // Step 3: Get the winning schedule for this slot's day
      const { schedule, isAfterEnd } = getActiveSchedule(date);

      const facilityDay = allData?.facilityHourSetting?.find(
        (f) => parseInt(f.day_id) === dayOfWeek,
      );

      // Step 4: No schedule → gray when provider selected
      if (!schedule) {
        if (hasProvider) return GRAY_SLOT;
        if (!facilityDay || String(facilityDay.is_closed) === '0') return GRAY_SLOT;
        const facOpen = parseTime(
          allData?.facilitySetting?.facility_first_hour,
          date,
        );
        const facClose = parseTime(
          allData?.facilitySetting?.facility_last_hour,
          date,
        );
        return isSlotOutsideHours(date, facOpen, facClose) ? GRAY_SLOT : {};
      }

      // Step 5: Regular schedule has expired → gray
      if (isAfterEnd) return GRAY_SLOT;

      // Step 6: Schedule says this day is closed → gray
      if (String(schedule.is_closed) === '0') return GRAY_SLOT;

      // Step 7: Check full-day block in allBlockData
      const isFullDayBlocked = allBlockData?.some(
        (item) =>
          parseInt(item.day_id) === dayOfWeek &&
          (!item.start || item.title === 'Full day Block'),
      );
      if (isFullDayBlocked) return GRAY_SLOT;

      // Step 8: Outside provider hours → gray (facility hours are viewport only)
      const workOpen = parseTime(schedule.open_time, date);
      const workClose = parseTime(schedule.close_time, date);
      if (!workOpen || !workClose) return GRAY_SLOT;
      if (isSlotOutsideHours(date, workOpen, workClose)) return GRAY_SLOT;

      return {};
    },
    [
      allData,
      allBlockData,
      getActiveSchedule,
      calPeriod.name,
      parseTime,
      hasProvider,
    ],
  );

  return (
    <div>
      {selectedEvent && <TitlePpup />}
      <div style={{ display: 'flex', margin: '10px', alignItems: 'center ' }}>
        <CalanderHeader
          locList={locList}
          handleLocation={handleLocation}
          selectedVal={appSearchPrams.clinicLocation}
        />
        <select
          className="appt-col-slct"
          value={appSearchPrams.allProviders}
          onChange={handleResource}
          style={{ width: '150px', marginLeft: '10px' }}
        >
          <option value="">All</option>
          {allResource &&
            allResource.map((note, idx) => {
              return (
                <option key={idx} value={note?.id}>
                  {note?.resName}
                </option>
              );
            })}
        </select>
        {calPeriodFilter?.name === 'day' && (
          <>
            <span className="clear-left10" style={{ width: '50px' }}>
              <ReactDatePicker
                selected={new Date(calPeriodFilter?.startDate)}
                onChange={(date) => handleChange(date)}
                customInput={
                  <img
                    src={'./images/calendar-icon.png'}
                    alt="Calendar"
                    width={'30px'}
                  />
                } // Use the image as the custom input
                dateFormat={DEFAULT_DATE_FORMATE}
                popperContainer={(popperProps) => (
                  <div style={{ position: 'relative', zIndex: 9999 }}>
                    {popperProps.children}
                  </div>
                )}
              />
            </span>
            {format(new Date(calPeriodFilter?.startDate), DEFAULT_DATE_FORMATE)}
            {calPeriodFilter.name === 'day' && (
              <span className="rbc-toolbar-labels clear-left">{`Provider timing:  ${
                typeof allData?.providerOfficeTime?.[0] === 'string' &&
                allData.providerOfficeTime[0].toLowerCase().trim() === 'closed'
                  ? 'Closed'
                  : `${formatTime(allData?.providerOfficeTime?.[0], defaultTimeFormat)} to ${formatTime(allData?.providerOfficeTime?.[1], defaultTimeFormat)}`
              }`}</span>
            )}
          </>
        )}
        <span
          className="actn-yes center"
          onClick={() => setAddTask(true)}
          style={{ width: '100px' }}
        >
          Add Task
        </span>
        {addTask && (
          <AddTaskPopup
            key={Math.random()}
            handleClose={() => {
              setAddTask(false);
              setEventData(null);
            }}
            handleReload={() => setReload(!reload)}
            eventData={eventData?.appData}
            fromCalFlag={true}
          />
        )}
        <span
          className="actn-yes center"
          style={{ width: '140px' }}
          onClick={() => setWeeklySchedule(true)}
        >
          Weekly schedule
        </span>
        <div className="right clear-left">
          <span>
            <span className="value">
              <input
                type="checkbox"
                className="clear-top5"
                onChange={() => setCancelCheck(!cancelCheck)}
                value={cancelCheck}
                checked={cancelCheck}
              />
            </span>
          </span>
          <span className="label clear-left5 clear-right ">
            View Canceled Appt.
          </span>
          <span>
            <span className="value clear-left">
              <input
                type="checkbox"
                className="clear-top5"
                onChange={() => setRescheduleCheck(!rescheduleCheck)}
                value={rescheduleCheck}
                checked={rescheduleCheck}
              />
            </span>
          </span>
          <span className="label clear-left5">View Reschedule Appt.</span>
        </div>
      </div>

      {combinedEvents && (
        <Calendar
          key={calTime?.scrliiTime || 'loading'}
          localizer={localizer}
          {...(defaultTimeFormat?.withoutSecond === 'HH:mm' && {
            formats: {
              timeGutterFormat: 'HH:mm', // 24hr time format
              eventTimeRangeFormat: ({ start, end }) =>
                `${start.toLocaleTimeString([], {
                  hour: '2-digit',
                  minute: '2-digit',
                  hour12: false, // 24hr format
                })} - ${end.toLocaleTimeString([], {
                  hour: '2-digit',
                  minute: '2-digit',
                  hour12: false, // 24hr format
                })}`,
            },
          })}
          defaultView={calPeriodFilter.name || 'week'}
          events={combinedEvents}
          startAccessor={(event) => {
            return new Date(event.start);
          }}
          date={new Date(calPeriodFilter?.startDate || new Date())}
          endAccessor={(event) => {
            return new Date(event.end);
          }}
          style={{ height: 500, margin: '20px 10px' }}
          titleAccessor={(event) => {
            if (event.isVirtual) return event.title;
            let title = event.title;
            if (event.app_status === '1') {
              title = 'No Show';
            } else if (event.app_status === '2') {
              title = 'Canceled';
            } else if (event.app_status === '3') {
              title = 'Rescheduled';
            }
            return title;
          }}
          onRangeChange={onRangeChange}
          views={['month', 'week', 'day']}
          selectable={true}
          onSelectSlot={handleSelectSlot}
          eventPropGetter={eventPropGetter}
          onDoubleClickEvent={onDoubleClickEvent}
          onSelectEvent={onSelectEvent}
          onNavigate={onNavigate}
          dayPropGetter={dayPropGetter}
          slotPropGetter={slotPropGetter}
          components={{ dayColumnWrapper }}
          min={calTime?.startTime ? parseTime(calTime?.startTime) : undefined} // Set the minimum time
          max={calTime?.endTime ? parseTime(calTime?.endTime) : undefined} // Set the maximum time
          toolbar={true}
          step={
            facilitySetting?.calander_default_slot
              ? parseInt(facilitySetting.calander_default_slot)
              : 15
          }
          scrollToTime={
            calTime?.scrliiTime ? parseTime(calTime?.scrliiTime) : null
          }
        />
      )}

      {weeklySchedule && (
        <WeeklySchedule
          handleClose={() => setWeeklySchedule(false)}
          reload={() => setReload(!reload)}
        />
      )}
      {isDialogOpen && (
        <AlertDialog
          successDialog={() => closeDialog()}
          title={'Alert'}
          bodytext={'Patient is already checked in.'}
        />
      )}
    </div>
  );
}

export default BigCalander;
